import { useTheme } from 'styled-components';

import { convertRemToPx, type Theme } from '@hivebrite/design-system/Theme';

import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { Wrapper } from 'hb-builder/components/common/Wrapper/WrapperRender';
import { StyledHtml } from 'hb-builder/components/UIComponents/shared/single/Text/StyledHtml';
import { getWrapperTextStyle } from 'hb-builder/components/UIComponents/shared/single/Text/utils/style';
import { useRendererContext } from 'hb-builder/hooks';
import type { RenderComponent } from 'hb-builder/types';
import { shouldDisplayTextComponentAsParagraph } from 'hb-builder/utils/shouldDisplayTextComponentAsParagraph';
import type {
  BUILDER_DOCUMENT_TYPE,
  DocumentItem,
} from 'hb-react/backoffice/types/builder/builderDocument';
import type { TextItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/textItemAttributes';

const componentType = 'single';

const Text: RenderComponent = () => <div />; // never used

const Render = ({
  children,
  item,
}: RenderProps<DocumentItem<TextItemAttributes>>) => {
  const { isMobilePreview } = useRendererContext();
  const theme = useTheme();

  const { attributes } = item || {};

  if (attributes?.hidden || !attributes) {
    return null;
  }

  return (
    // @ts-expect-error untyped
    <Wrapper
      attributes={attributes}
      componentType={componentType}
      dataTestId="builder-component-text"
      isMobilePreview={isMobilePreview}
      style={{
        ...getWrapperTextStyle({
          attributes,
          canUpdateTextBackground: true,
          theme,
        }),
        margin: '0',
      }}
    >
      <StyledHtml
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: children }}
      />
    </Wrapper>
  );
};

Text.componentName = 'Text';
Text.Render = Render;

interface DefaultConfigArguments {
  theme?: Theme;
  builderType?: BUILDER_DOCUMENT_TYPE;
}

const defaultConfig = ({ theme, builderType }: DefaultConfigArguments) => {
  const defaultFontSize = theme
    ? convertRemToPx(parseInt(theme.fontSizes[2], 10))
    : undefined;

  return {
    containerPadding: '10px 10px 10px 10px',
    content: `<p style="line-height: 22px; text-align: center"><span style="font-size: 14px;">${
      shouldDisplayTextComponentAsParagraph(builderType)
        ? 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. '
        : __('Edit your text with the toolbar above')
    }</span></p>`,
    horizontalAlign: 'left',
    letterSpacing: 0,
    textFontSize: `${defaultFontSize}px`,
  };
};

Text.item = ({ features, theme, builderType }) => {
  const textConfig = {
    ...defaultConfig({ theme, builderType }),
    ...(features?.text || {}),
  };

  return {
    attributes: {
      containerPadding: textConfig.containerPadding,
      defaultTextColor: textConfig.defaultTextColor,
      hidden: textConfig.hidden,
      horizontalAlign: textConfig.horizontalAlign,
      textColor: textConfig.textColor,
      textLetterSpacing: textConfig.letterSpacing,
      textFontFamily: textConfig.fontFamily,
      textFontSize: textConfig.textFontSize,
      tied: textConfig.tied,
    },
    category: 'single',
    content: textConfig.content,
    name: Text.componentName,
    type: Text.componentName,
  };
};

export default Text;
